import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ChevronLeft, Plus, X, Menu } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import PensionSelectionForm from "./PensionSelectionForm";
import MenuBar from "@/components/MenuBar";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  Employee,
  EnrollmentDetailsResponse,
  SubmitSelectionsRequest,
  PensionSelectionFormData,
} from "@/types/pensions";
import { formatDateToBritish } from "@/lib/dateUtils";

interface PensionDetailsProps {
  employee: Employee;
  onBack: () => void;
  selectedUserId?: string;
}

const PensionDetails = ({ employee, onBack, selectedUserId }: PensionDetailsProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrollmentDetails, setEnrollmentDetails] =
    useState<EnrollmentDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchEnrollmentDetails();
  }, [employee.employeeId]);

  const fetchEnrollmentDetails = async () => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId) {
        queryParams.userId = selectedUserId;
      }

      const data = await ApiService.request<EnrollmentDetailsResponse>(
        EndpointKeys.FETCH_ENROLLMENT_DETAILS,
        {
          urlParams: { id: employee.employeeId.toString() },
          queryParams,
        },
      );
      setEnrollmentDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitSelection = async (formData: PensionSelectionFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (!enrollmentDetails?.enrollmentDetails) return;

      const queryParams: Record<string, string | number> = {};
      if (selectedUserId) {
        queryParams.userId = selectedUserId;
      }

      const requestBody: SubmitSelectionsRequest = formData.action === "optOut"
        ? {
            action: "optOut",
            effectiveDate: formData.effectiveDate,
            reason: formData.optOutReason || "",
            benefitLineId: formData.benefitLineId 
              ? parseInt(formData.benefitLineId) 
              : undefined,
          }
        : {
            action: "enroll",
            effectiveDate: formData.effectiveDate,
            selections: [
              {
                benefitLineId: parseInt(formData.benefitLineId),
                employerContributionPercent: formData.employerContributionPercent || 0,
                employeeContributionPercent: formData.employeeContributionPercent || 0,
                isReliefAtSource: formData.isReliefAtSource || true,
              },
            ],
          };

      await ApiService.request(EndpointKeys.SUBMIT_SELECTIONS, {
        method: "POST",
        urlParams: {
          id: enrollmentDetails.enrollmentDetails.event.id.toString(),
        },
        queryParams,
        body: requestBody,
      });

      // Refresh data to show the updated state
      await fetchEnrollmentDetails();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit selections");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!enrollmentDetails?.enrollmentDetails || error) {
    return (
      <div className="p-4 sm:p-6">
        <Alert variant="destructive">
          <AlertDescription>
            {error || "Failed to load details"}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const details = enrollmentDetails.enrollmentDetails;
  const payrunValidation = enrollmentDetails.payrunValidation;
  const currentStatus =
    details.currentSelections.length > 0
      ? {
          isOptedOut: false,
          benefitLineId: details.currentSelections[0].benefitLineId,
          currentScheme:
            details.availableOptions.find(
              (option) =>
                option.id === details.currentSelections[0].benefitLineId,
            )?.displayName || "NEST",
          employerContribution: parseFloat(
            details.currentSelections[0].employerContributionPercent,
          ),
          employeeContribution: parseFloat(
            details.currentSelections[0].employeeContributionPercent,
          ),
        }
      : {
          isOptedOut: details.metadata?.isOptedOut,
          optOutDate: details.metadata?.optOutDate,
          optOutReason: "Opted Out",
        };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0">
        <div className="border-b p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="self-start"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl md:text-2xl font-bold">
              {`${employee.firstName} ${employee.lastName}`} - Pension Details
            </h1>
          </div>
        </div>

        <div className="p-4 md:p-6 max-w-4xl">
          <div className="space-y-4 md:space-y-6">
            {/* Eligibility Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base md:text-lg">
                  Eligibility Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge
                    variant={
                      enrollmentDetails.eligibility.isPensionEligible
                        ? "default"
                        : "secondary"
                    }
                  >
                    {enrollmentDetails.eligibility.isPensionEligible
                      ? "Eligible"
                      : "Not Eligible"}
                  </Badge>
                </div>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">
                    Auto Enrollment
                  </span>
                  <Badge
                    variant={
                      enrollmentDetails.eligibility.isAutoEnrollmentRequired
                        ? "default"
                        : "secondary"
                    }
                  >
                    {enrollmentDetails.eligibility.isAutoEnrollmentRequired
                      ? "Required"
                      : "Not Required"}
                  </Badge>
                </div>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">
                    Eligibility Date
                  </span>
                  <span className="text-sm">
                    {format(
                      new Date(
                        enrollmentDetails.eligibility.pensionEligibilityDate.slice(
                          0,
                          -1,
                        ),
                      ),
                      "PP",
                    )}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Enrollment Form or Status */}
            <div className="space-y-4">
              {isEditing ? (
                <PensionSelectionForm
                  currentStatus={currentStatus}
                  eventId={details.event.id}
                  availableOptions={details.availableOptions}
                  payrunValidation={enrollmentDetails.payrunValidation}
                  onSubmit={handleSubmitSelection}
                  isSubmitting={isSubmitting}
                  error={error}
                  isEditing={true}
                />
              ) : (
                <Card>
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <CardTitle className="text-base md:text-lg">
                        Pension Enrollment Status
                      </CardTitle>
                      <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
                        {currentStatus.isOptedOut ? (
                          <Badge
                            variant="destructive"
                            className="flex items-center gap-2"
                          >
                            <X className="h-4 w-4" />
                            Opted Out
                          </Badge>
                        ) : details.currentSelections.length > 0 ? (
                          <Badge variant="default" className="bg-green-600">
                            Enrolled
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                        {details.canModify && !isEditing && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setIsEditing(true)}
                            className="w-full md:w-auto"
                          >
                            Change Enrollment
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {currentStatus.isOptedOut ? (
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="font-medium">Currently Opted Out</div>
                          <div className="mt-2 text-sm text-muted-foreground">
                            {currentStatus.optOutDate && (
                              <>
                                Opted out on{" "}
                                {format(
                                  new Date(currentStatus.optOutDate),
                                  "PPP",
                                )}
                                <br />
                              </>
                            )}
                            Reason: {currentStatus.optOutReason}
                          </div>
                        </AlertDescription>
                      </Alert>
                    ) : details.currentSelections.length > 0 ? (
                      <Alert>
                        <AlertDescription>
                          <div className="font-medium">Current Enrollment</div>
                          <div className="mt-2 space-y-1 text-sm">
                            <div>Scheme: {currentStatus.currentScheme}</div>
                            <div>
                              Contributions:{" "}
                              {currentStatus.employerContribution}% employer,{" "}
                              {currentStatus.employeeContribution}% employee
                            </div>
                          </div>
                        </AlertDescription>
                      </Alert>
                    ) : null}
                  </CardContent>
                </Card>
              )}
            </div>

            {!isEditing &&
              payrunValidation.hasApprovedPayruns &&
              payrunValidation.earliestChangeDate && (
                <Alert>
                  <AlertDescription className="text-sm">
                    Changes to pension enrollment will take effect from{" "}
                    {formatDateToBritish(
                      payrunValidation.earliestChangeDate + "T00:00:00",
                    )}{" "}
                    due to approved payruns.
                  </AlertDescription>
                </Alert>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PensionDetails;
